// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@flip/App/Store/Reducers";
import {
    color,
    colorText,
    layoutContainer,
    linkArrowStyle,
    mergeWithCss,
    xScreen,
} from "@flip/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonPresentationProps, ButtonSizeVariants } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

export interface BlockCardProp {
    image?: string;
    mobileSpecificImage?: string;
    imagePosition?: "left" | "right";
    title: string;
    titleColor?: "black" | "white";
    description:
        | string
        | React.DetailedReactHTMLElement<{}, HTMLElement>
        | React.DetailedReactHTMLElement<{}, HTMLElement>[];
    descriptionColor?: "black" | "white";
    link: LinkFieldValue;
    buttonVariant?: "primary" | "secondary" | "tertiary" | "link";
    buttonLabel?: string;
    backgroundColor?: string;
    extendedStyles?: BlockCardStyles;
    buttonSizeVariant?: ButtonSizeVariants;
}

export interface BlockCardStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    link?: LinkPresentationProps;
    button?: ButtonPresentationProps;
}

export const blockCardStyles: BlockCardStyles = {
    container: {
        css: css``,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
            &&&& {
                padding: 0;
            }
            ${({ imagePosition }: any) =>
                css`
                    ${layoutContainer}
                    ${imagePosition &&
                    css`
                        flex-direction: ${imagePosition === "right" ? "row-reverse" : "row"};
                    `};
                `};

            ${xScreen("lte", "md")} {
                flex-direction: column;
                height: 100%;
                align-items: center;
            }
            ${xScreen("lte", "xs")} {
                align-items: center;
                &&&& {
                    padding-left: 0;
                    padding-right: 0;
                }
            }
        `,
    },
    image: {
        css: css`
            width: 50%;
            ${xScreen("lte", "md")} {
                width: 100%;
                ${({ imagePosition }: any) => css`
                margin-${imagePosition === "right" ? "left" : "right"}: initial;
                `}
            }
            ${xScreen("lte", "xs")} {
                width: auto;
            }
        `,
    },
    infoWrapper: {
        css: css`
            ${({ backgroundColor }: any) =>
                css`
                    ${backgroundColor &&
                    css`
                        width: 50%;
                        background-color: ${color(backgroundColor)};
                        padding: 32px 16px;
                    `}
                `};

            ${xScreen("lte", "md")} {
                width: 100%;
                padding: 24px;
            }
            ${xScreen("lte", "xs")} {
                width: auto;
                padding: 16px;
            }
        `,
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 24px;
        `,
    },
    description: {
        as: "p",
        css: css`
            margin-bottom: 0;
        `
    },
    link: {
        ...linkArrowStyle,
    },
    button: {
        css: css`
            margin-top: 24px;
        `,
    },
};

type Props = BlockCardProp;

const BlockCard: React.FC<Props> = ({
    image,
    title,
    description,
    imagePosition,
    link,
    buttonVariant,
    buttonLabel,
    mobileSpecificImage,
    backgroundColor,
    descriptionColor,
    extendedStyles,
    buttonSizeVariant,
}) => {
    const [styles] = React.useState(() => mergeWithCss(blockCardStyles, extendedStyles));
    const url = useGetLink(link).url;
    const history = useHistory();
    const isMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);

    const openLinkHandler = () => url && history.push(url);

    const elementProps = {
        backgroundColor,
        descriptionColor,
        imagePosition,
    };

    return (
        <StyledWrapper {...styles.container} {...elementProps} data-test-selector="block_card">
            {title && (
                <Typography data-test-selector="block_card_title" {...styles.title}>
                    {colorText(title)}
                </Typography>
            )}
            <StyledWrapper {...styles.wrapper} {...elementProps}>
                {image && (
                    <LazyImage
                        src={isMobileScreen ? mobileSpecificImage || image : image}
                        altText={title}
                        {...styles.image}
                        {...elementProps}
                    />
                )}
                <StyledWrapper {...elementProps} {...styles.infoWrapper} data-test-selector="block_card_info">
                    <Typography
                        data-test-selector="block_card_description"
                        color={descriptionColor}
                        {...elementProps}
                        {...styles.description}
                    >
                        {typeof description === "string" ? parse(description, parserOptions) : description}
                    </Typography>
                    {url && buttonLabel && (
                        <>
                            {buttonVariant !== "link" ? (
                                <Button
                                    {...styles.button}
                                    variant={buttonVariant}
                                    aria-label={buttonLabel}
                                    sizeVariant={buttonSizeVariant}
                                    onClick={openLinkHandler}
                                >
                                    {buttonLabel}
                                </Button>
                            ) : (
                                <Link onClick={openLinkHandler} aria-label={buttonLabel} {...styles.link}>
                                    {buttonLabel}
                                </Link>
                            )}
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

BlockCard.defaultProps = {
    backgroundColor: "common.ui10",
    descriptionColor: "black",
    titleColor: "black",
    buttonSizeVariant: "large",
};

export default BlockCard;
